import { TechnologyItem } from '@tribal-worldwide-london/fa-product-service-vwg';
import { VideoModal } from '@tribal-worldwide-london/ui-component-library-vwg';
import {
  DesignTokenSizeEnum,
  Text,
  TokenTextAppearance,
  TextTag,
} from '@volkswagen-onehub/components-core';
import { Play } from '@volkswagen-onehub/icons-core';
import { useState } from 'react';
import {
  handleVideoEnded,
  handleVideoMilestone,
  handleVideoPause,
  handleVideoPlay,
} from '@/analytics/eventHelpers/video';
import { SectionNames } from '@/analytics/sectionNames';
import { useTribalContext } from '@/context/tribal';
import { useFeatures } from '@/hooks/useFeatures';
import AutoScrollContainer from '@/modules/components/AutoScrollContainer/AutoScrollContainer';
import Box from '@/modules/components/Box/Box';
import FeatureBox from '@/modules/components/FeatureBox/FeatureBox';
import SectionHeader from '@/modules/components/SectionHeader/SectionHeader';
import { NAVIGATION_HEIGHT } from '@/modules/constants/navigation';
import { removeDuplicatesByKey } from '@/modules/utils/object';
import {
  getSelectedVideo,
  technologyItemState,
} from '@/modules/utils/storeUtils';
import useGlobalStore from '@/stores/globalStore';

const TechnologyVideos = () => {
  const { contentManager, analyticsManager, analyticsBuilder } =
    useTribalContext();
  const [videoId, setVideoId] = useState('');
  const activeSlideIndex = useGlobalStore((state) => state.activeSlideIndex);
  const { configurations, retailers } = useGlobalStore((state) => state.app);

  const { getSpecItemLabel } = useFeatures();

  const extrasTechnologyVideos = configurations
    .map(
      (configuration) =>
        configuration?.productData?.technologyItems?.extras ?? []
    )
    .flat();

  const standardTechnologyVideos = configurations
    .map(
      (configuration) =>
        configuration?.productData?.technologyItems?.standard ?? []
    )
    .flat();

  let technologyVideos = removeDuplicatesByKey(
    [...extrasTechnologyVideos, ...standardTechnologyVideos],
    'id'
  );

  const analyticsCommonFields = {
    sectionName: SectionNames.COMPARE_PAGE_TECHNOLOGY_VIDEOS,
    configurations,
    retailers,
    analyticsManager,
    analyticsBuilder,
  };

  const selectedVideoDetails = getSelectedVideo(technologyVideos, videoId);

  const title = selectedVideoDetails?.name || null;

  return (
    <>
      <Box
        mb={DesignTokenSizeEnum.static370}
        b960={{ mb: DesignTokenSizeEnum.static530 }}
      >
        <SectionHeader
          title={contentManager.get(
            'compare-technology-videos-section-title-normal'
          )}
          titleSize={TokenTextAppearance.headline400}
          titleBold={contentManager.get(
            'compare-technology-videos-section-title-bold'
          )}
          description={contentManager.get(
            'compare-technology-videos-section-subtitle'
          )}
          descriptionSize={TokenTextAppearance.bigcopy100}
        />
      </Box>
      {technologyVideos.map((item: TechnologyItem) => {
        const videoUrl = item.assetCollection?.[0]?.url || '';
        if (!videoUrl) return;

        return (
          <Box key={item.id} mb={DesignTokenSizeEnum.static400}>
            <Box
              display="flex"
              alignItems="center"
              gap={DesignTokenSizeEnum.static150}
              mb={DesignTokenSizeEnum.static150}
              cursor="pointer"
              onClick={() => setVideoId(videoUrl)}
            >
              <Box
                as={Text}
                tag={TextTag.h3}
                appearance={TokenTextAppearance.bigcopy100}
                bold
                mb={DesignTokenSizeEnum.static0}
              >
                {item.name}
              </Box>

              <Box
                border="1px solid black"
                borderRadius="100%"
                width="25px"
                height="25px"
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexShrink="0"
              >
                <Play variant="small" />
              </Box>
            </Box>

            <Box
              as={Text}
              tag={TextTag.p}
              appearance={TokenTextAppearance.bigcopy100}
              mb={DesignTokenSizeEnum.static150}
            >
              {item.subtitle}
            </Box>
            <AutoScrollContainer
              activeSlideIndex={activeSlideIndex}
              responsiveProps={{
                mobile: {
                  slidesPerPage: 2,
                  gap: DesignTokenSizeEnum.static150,
                },
                desktop: {
                  slidesPerPage: 3,
                  gap: DesignTokenSizeEnum.static300,
                },
              }}
            >
              {configurations.map((configuration) => {
                const availability = technologyItemState(item, configuration);

                return (
                  <FeatureBox
                    status={availability}
                    key={configuration.modelTitle}
                  >
                    {getSpecItemLabel(availability)}
                  </FeatureBox>
                );
              })}
            </AutoScrollContainer>
          </Box>
        );
      })}
      <VideoModal
        isOpen={!!videoId}
        handleClose={() => setVideoId('')}
        videoId={videoId}
        goBackLabel={contentManager.get('ctas-go-back')}
        playerName="youtube-video-embed"
        autoplay
        controls
        navigationHeight={NAVIGATION_HEIGHT}
        videoPlayCallback={(fields) => {
          handleVideoPlay(analyticsCommonFields, {
            ...fields,
            title,
          });
        }}
        videoPauseCallback={(fields) =>
          handleVideoPause(analyticsCommonFields, {
            ...fields,
            title,
          })
        }
        videoEndedCallback={(fields) =>
          handleVideoEnded(analyticsCommonFields, {
            ...fields,
            title,
          })
        }
        videoMilestoneCallback={(fields) => 
          handleVideoMilestone(analyticsCommonFields, {
            ...fields,
            title,
          })
        }
      />
    </>
  );
};

export default TechnologyVideos;
